import { useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { DESARROLLOS_TENANT_ID } from './tenant';
import SivarSendWordmark from '../components/brand/SivarSendWordmark';

const LoginDesarrollos = ({ onClientLogin, onOperatorLogin }) => {
  const [mode, setMode] = useState('client'); // 'client' | 'operator'

  const [lastFour, setLastFour] = useState('');
  const [clientError, setClientError] = useState('');
  const [clientLoading, setClientLoading] = useState(false);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [opError, setOpError] = useState('');
  const [opLoading, setOpLoading] = useState(false);

  // Cliente side: the last 4 digits now resolve a real customers row in the
  // Desarrollos tenant. This is still not authentication — there is no cliente
  // Supabase session — but the identity that comes out of it is real, so an
  // exec registered on the spot from the Clientes panel can log straight in.
  const handleClientSubmit = async (e) => {
    e.preventDefault();
    if (clientLoading) return;
    setClientError('');

    const digits = lastFour.replace(/\D/g, '');
    if (digits.length !== 4) {
      setClientError('Ingresá los 4 dígitos de tu teléfono.');
      return;
    }

    setClientLoading(true);
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, full_name, phone_number, tenant_id')
        .eq('tenant_id', DESARROLLOS_TENANT_ID)
        .filter('phone_number', 'ilike', `%${digits}`)
        .is('deleted_at', null)
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setClientError('No encontramos tu cuenta. Pediles a un operador que te registre.');
        setLastFour('');
        return;
      }

      onClientLogin({ id: data.id, full_name: data.full_name, phone_number: data.phone_number });
    } catch (err) {
      console.error('[LoginDesarrollos] error buscando el cliente', err);
      setClientError('No pudimos verificar tus datos. Revisá tu señal y probá de nuevo.');
    } finally {
      setClientLoading(false);
    }
  };

  // Real Supabase Auth, then two guards: the JWT's tenant_id must be Desarrollos,
  // and the user must hold an admin row in staff_members. Any failure signs the
  // session back out so a rejected login never leaves a live session behind.
  const handleOperatorSubmit = async (e) => {
    e.preventDefault();
    if (opLoading) return;
    setOpError('');
    setOpLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: username.trim().toLowerCase(),
        password,
      });

      if (error) {
        // Deliberately generic in the UI — Supabase's own text leaks whether
        // the account exists.
        console.error('[LoginDesarrollos] auth error', error);
        setOpError('Usuario o contraseña incorrectos.');
        return;
      }

      // Propagate the session explicitly before the RLS-guarded query below.
      // Without this the staff_members SELECT can fire before the client has
      // adopted the new token, auth.uid() resolves NULL, the policy hides the
      // row, and a legitimate admin is rejected for "no permissions".
      // LoginPortal.js does the same thing for the same reason.
      await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });

      const user = data.session.user;

      if (user.app_metadata?.tenant_id !== DESARROLLOS_TENANT_ID) {
        await supabase.auth.signOut();
        setOpError('Esta cuenta no pertenece a este tenant.');
        return;
      }

      const { data: staff, error: staffError } = await supabase
        .from('staff_members')
        .select('id, role')
        .eq('id', user.id)
        .single();

      if (staffError || staff?.role !== 'admin') {
        await supabase.auth.signOut();
        setOpError('No tenés permisos de operador en Desarrollos.');
        return;
      }

      onOperatorLogin();
    } finally {
      setOpLoading(false);
    }
  };

  const clientActive = mode === 'client';

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-5">
      <div className="w-full max-w-[400px] bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-10">
        {/* ── Wordmark ─────────────────────────────────────────────────── */}
        <SivarSendWordmark size="lg" subtitle="Desarrollos" className="text-center mb-8" />

        {/* ── Crossfading client / operator panels (CSS grid stack) ──────── */}
        <div className="relative grid">
          {/* Client panel */}
          <form
            onSubmit={handleClientSubmit}
            className={`col-start-1 row-start-1 flex flex-col gap-4 transition-all duration-[280ms] ease-out ${
              clientActive
                ? 'translate-x-0 opacity-100'
                : '-translate-x-full opacity-0 pointer-events-none'
            }`}
          >
            <p className="text-sm text-slate-500 text-center leading-snug">
              Ingresá los últimos 4 dígitos de tu teléfono para empezar.
            </p>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={4}
              autoComplete="off"
              value={lastFour}
              onChange={(e) => setLastFour(e.target.value.replace(/\D/g, ''))}
              placeholder="0000"
              className="w-full h-16 text-center text-3xl font-bold tracking-[0.5em] text-slate-900 bg-[#f8fafc] border border-slate-200 rounded-lg outline-none focus:border-[var(--brand-teal)] transition-colors"
            />
            {clientError && (
              <p className="text-xs text-red-600 text-center">{clientError}</p>
            )}
            <button
              type="submit"
              disabled={clientLoading}
              className="w-full h-11 bg-[var(--brand-teal)] hover:brightness-[1.08] disabled:opacity-70 text-white font-semibold text-sm rounded-lg flex items-center justify-center gap-2 transition"
            >
              {clientLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Buscando…
                </>
              ) : (
                <>
                  Entrar <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {/* Operator panel — always mounted so saved-credential autofill fires immediately */}
          <form
            onSubmit={handleOperatorSubmit}
            className={`col-start-1 row-start-1 flex flex-col gap-4 transition-all duration-[280ms] ease-out ${
              !clientActive
                ? 'translate-x-0 opacity-100'
                : 'translate-x-full opacity-0 pointer-events-none'
            }`}
          >
            <p className="text-sm text-slate-500 text-center leading-snug">Acceso operador</p>

            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Usuario
              </label>
              <input
                type="email"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="nombre@sivarsend.app"
                className="w-full h-11 px-3 text-sm text-slate-900 bg-[#f8fafc] border border-slate-200 rounded-lg outline-none focus:border-[var(--brand-teal)] transition-colors"
                tabIndex={clientActive ? -1 : 0}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Contraseña
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-11 px-3 text-sm text-slate-900 bg-[#f8fafc] border border-slate-200 rounded-lg outline-none focus:border-[var(--brand-teal)] transition-colors"
                tabIndex={clientActive ? -1 : 0}
              />
            </div>

            {opError && <p className="text-xs text-red-600 text-center">{opError}</p>}

            <button
              type="submit"
              disabled={opLoading}
              tabIndex={clientActive ? -1 : 0}
              className="w-full h-11 bg-[var(--brand-teal)] hover:brightness-[1.08] disabled:brightness-100 disabled:opacity-70 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-lg flex items-center justify-center gap-2 transition"
            >
              {opLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Ingresando…
                </>
              ) : (
                'Entrar al Sistema'
              )}
            </button>

            <button
              type="button"
              tabIndex={clientActive ? -1 : 0}
              onClick={() => setMode('client')}
              className="text-[11px] text-slate-400 hover:text-slate-600 text-center transition-colors"
            >
              ← Volver a rastreo
            </button>
          </form>
        </div>

        {/* ── Footer with hidden operator trigger ─────────────────────────── */}
        <div className="h-px bg-slate-200 mt-8 mb-4" />
        <p className="text-center text-[10px] text-slate-400">
          SivarSend ·{' '}
          <span className="cursor-pointer" onClick={() => setMode('operator')}>
            para
          </span>{' '}
          los encomendistas
        </p>
      </div>
    </div>
  );
};

export default LoginDesarrollos;
